import { useState, useEffect, createContext, useContext, useCallback } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, useNavigate, Link } from "react-router-dom";
import axios from "axios";
import { Toaster, toast } from "sonner";
import { 
  User, Trophy, Gamepad2, LogOut, Play, 
  Flame, Clock, Target, Crown, Star,
  Image, HelpCircle, X, Menu, ChevronRight,
  Share2, Zap, Volume2, VolumeX
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Sound Effects (using Web Audio API for simple sounds)
const createSound = (frequency, duration, type = 'sine') => {
  return () => {
    try {
      const audioContext = new (window.AudioContext || window.webkitAudioContext)();
      const oscillator = audioContext.createOscillator();
      const gainNode = audioContext.createGain();
      
      oscillator.connect(gainNode);
      gainNode.connect(audioContext.destination);
      
      oscillator.frequency.value = frequency;
      oscillator.type = type;
      gainNode.gain.setValueAtTime(0.3, audioContext.currentTime);
      gainNode.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + duration);
      
      oscillator.start(audioContext.currentTime);
      oscillator.stop(audioContext.currentTime + duration);
    } catch (e) {
      // Audio not supported
    }
  };
};

const sounds = {
  correct: createSound(880, 0.15, 'sine'),  // High beep
  wrong: createSound(220, 0.3, 'sawtooth'), // Low buzz
  tick: createSound(440, 0.05, 'sine'),     // Timer tick
  gameOver: () => {
    createSound(440, 0.1)();
    setTimeout(() => createSound(330, 0.1)(), 100);
    setTimeout(() => createSound(262, 0.2)(), 200);
  },
  streak: createSound(660, 0.1, 'sine'),    // Streak sound
};

// Auth Context
const AuthContext = createContext(null);

const useAuth = () => useContext(AuthContext);

const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem("token"));
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (token) {
      axios.defaults.headers.common["Authorization"] = `Bearer ${token}`;
      fetchUser();
    } else {
      setLoading(false);
    }
  }, [token]);

  const fetchUser = async () => {
    try {
      const res = await axios.get(`${API}/auth/me`);
      setUser(res.data);
    } catch (e) {
      logout();
    } finally {
      setLoading(false);
    }
  };

  const login = (newToken, userData) => {
    localStorage.setItem("token", newToken);
    axios.defaults.headers.common["Authorization"] = `Bearer ${newToken}`;
    setToken(newToken);
    setUser(userData);
  };

  const logout = () => {
    localStorage.removeItem("token");
    delete axios.defaults.headers.common["Authorization"];
    setToken(null);
    setUser(null);
  };

  return (
    <AuthContext.Provider value={{ user, token, login, logout, loading, fetchUser }}>
      {children}
    </AuthContext.Provider>
  );
};

// Auth Modal Component
const AuthModal = ({ isOpen, onClose }) => {
  const [mode, setMode] = useState("login");
  const [formData, setFormData] = useState({ username: "", email: "", password: "" });
  const [loading, setLoading] = useState(false);
  const { login } = useAuth();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      if (mode === "guest") {
        const res = await axios.post(`${API}/auth/guest`, { username: formData.username || "Guest" });
        login(res.data.token, res.data.user);
        toast.success("Welcome, Guest!");
      } else if (mode === "login") {
        const res = await axios.post(`${API}/auth/login`, { 
          email: formData.email, 
          password: formData.password 
        });
        login(res.data.token, res.data.user);
        toast.success("Welcome back!");
      } else {
        const res = await axios.post(`${API}/auth/register`, formData);
        login(res.data.token, res.data.user);
        toast.success("Account created!");
      }
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Something went wrong");
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="auth-modal animate-fade-in" data-testid="auth-modal">
      <div className="auth-form animate-slide-up">
        <div className="flex justify-between items-center mb-8">
          <h2 className="font-anton text-2xl uppercase tracking-wide text-[var(--primary)]">
            {mode === "login" ? "Sign In" : mode === "register" ? "Create Account" : "Quick Play"}
          </h2>
          <button onClick={onClose} className="btn-ghost" data-testid="close-auth-modal">
            <X size={24} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">
          {mode !== "login" && (
            <input
              type="text"
              placeholder="Username"
              className="cyber-input"
              value={formData.username}
              onChange={(e) => setFormData({ ...formData, username: e.target.value })}
              data-testid="username-input"
            />
          )}
          {mode !== "guest" && (
            <>
              <input
                type="email"
                placeholder="Email"
                className="cyber-input"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                required
                data-testid="email-input"
              />
              <input
                type="password"
                placeholder="Password"
                className="cyber-input"
                value={formData.password}
                onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                required
                data-testid="password-input"
              />
            </>
          )}
          <button 
            type="submit" 
            className="btn-primary w-full" 
            disabled={loading}
            data-testid="auth-submit-btn"
          >
            {loading ? "Loading..." : mode === "guest" ? "Play as Guest" : mode === "login" ? "Sign In" : "Create Account"}
          </button>
        </form>

        <div className="mt-6 space-y-3">
          {mode !== "guest" && (
            <button 
              className="btn-secondary w-full"
              onClick={() => setMode("guest")}
              data-testid="guest-mode-btn"
            >
              <User size={18} /> Play as Guest
            </button>
          )}
          <div className="text-center text-[var(--neutral-500)]">
            {mode === "login" ? (
              <span>New here? <button className="text-[var(--primary)] hover:underline" onClick={() => setMode("register")}>Create account</button></span>
            ) : mode === "register" ? (
              <span>Have an account? <button className="text-[var(--primary)] hover:underline" onClick={() => setMode("login")}>Sign in</button></span>
            ) : (
              <span>Want to save progress? <button className="text-[var(--primary)] hover:underline" onClick={() => setMode("register")}>Create account</button></span>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

// Navigation Component
const Navigation = ({ onAuthClick }) => {
  const { user, logout } = useAuth();
  const [menuOpen, setMenuOpen] = useState(false);

  return (
    <nav className="nav-container" data-testid="main-nav">
      <div className="max-w-7xl mx-auto flex items-center justify-between">
        <Link to="/" className="flex items-center gap-3">
          <Gamepad2 className="text-[var(--primary)]" size={28} />
          <span className="font-anton text-xl uppercase tracking-wider hidden sm:block">
            Guess<span className="text-[var(--primary)]">TheAnime</span>
          </span>
        </Link>

        <div className="hidden md:flex items-center gap-6">
          <Link to="/" className="btn-ghost flex items-center gap-2" data-testid="nav-play">
            <Play size={18} /> Play
          </Link>
          <Link to="/leaderboard" className="btn-ghost flex items-center gap-2" data-testid="nav-leaderboard">
            <Trophy size={18} /> Leaderboard
          </Link>
          {user ? (
            <div className="flex items-center gap-4">
              <Link to="/profile" className="flex items-center gap-2 text-[var(--neutral-100)]" data-testid="nav-profile">
                <div className="w-8 h-8 rounded-full bg-[var(--primary)] flex items-center justify-center text-black font-bold">
                  {user.username[0].toUpperCase()}
                </div>
                <span className="font-arcade text-xs">{user.total_score}</span>
              </Link>
              <button onClick={logout} className="btn-ghost" data-testid="logout-btn">
                <LogOut size={18} />
              </button>
            </div>
          ) : (
            <button onClick={onAuthClick} className="btn-primary" data-testid="sign-in-btn">
              Sign In
            </button>
          )}
        </div>

        <button className="md:hidden btn-ghost" onClick={() => setMenuOpen(!menuOpen)}>
          <Menu size={24} />
        </button>
      </div>

      {menuOpen && (
        <div className="md:hidden mt-4 pb-4 border-t border-white/10 pt-4 space-y-4 animate-slide-up">
          <Link to="/" className="block btn-ghost" onClick={() => setMenuOpen(false)}>Play</Link>
          <Link to="/leaderboard" className="block btn-ghost" onClick={() => setMenuOpen(false)}>Leaderboard</Link>
          {user ? (
            <>
              <Link to="/profile" className="block btn-ghost" onClick={() => setMenuOpen(false)}>Profile</Link>
              <button onClick={() => { logout(); setMenuOpen(false); }} className="block btn-ghost">Logout</button>
            </>
          ) : (
            <button onClick={() => { onAuthClick(); setMenuOpen(false); }} className="btn-primary w-full">Sign In</button>
          )}
        </div>
      )}
    </nav>
  );
};

// Game Component
const GameScreen = ({ mode, genre, difficulty }) => {
  const { user, fetchUser } = useAuth();
  const [question, setQuestion] = useState(null);
  const [nextQuestion, setNextQuestion] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedAnswer, setSelectedAnswer] = useState(null);
  const [result, setResult] = useState(null);
  const [isSubmittingAnswer, setIsSubmittingAnswer] = useState(false);
  const [score, setScore] = useState(0);
  const [streak, setStreak] = useState(0);
  const [highestStreak, setHighestStreak] = useState(0);
  const [questionCount, setQuestionCount] = useState(0);
  const [correctAnswers, setCorrectAnswers] = useState(0);
  const [accuracy, setAccuracy] = useState(0);
  const [rank, setRank] = useState("C");
  const [resultMessage, setResultMessage] = useState("");
  const [gameStartTime, setGameStartTime] = useState(null);
  const [gameOver, setGameOver] = useState(false);
  const [gameResetKey, setGameResetKey] = useState(0);
  const [loadError, setLoadError] = useState(false);
  const [retryCount, setRetryCount] = useState(0);
  const [soundEnabled, setSoundEnabled] = useState(true);
  
  // Use passed difficulty or fallback to medium
  const actualDifficulty = difficulty;
  
  // Difficulty settings
  const difficultySettings = {
    easy: { timeLimit: 45, multiplier: 0.8 },
    medium: { timeLimit: 30, multiplier: 1.0 },
    hard: { timeLimit: 20, multiplier: 1.5 }
  };
  const settings = difficultySettings[actualDifficulty];
  const [timeLeft, setTimeLeft] = useState(settings.timeLimit);

  const playSound = (soundName) => {
    if (soundEnabled && sounds[soundName]) {
      sounds[soundName]();
    }
  };

  const fetchQuestion = async (isRetry = false) => {
    setLoading(true);
    setSelectedAnswer(null);
    setResult(null);
    setIsSubmittingAnswer(false);
    setLoadError(false);
    
    try {
      const params = { mode, difficulty };
      if (genre && genre !== "top100") params.genre = genre;
      // For top100, we use easy difficulty page range (1-4) 
      if (genre === "top100") params.difficulty = "easy";
      
      const res = await axios.get(`${API}/game/question`, { params, timeout: 15000 });
      setSelectedAnswer(null);
      setResult(null);
      setIsSubmittingAnswer(false);
      setQuestion(res.data);
      setNextQuestion(null);
      setTimeLeft(settings.timeLimit);
      setGameStartTime(Date.now());
      setRetryCount(0);
      
      // Start preloading the following question in the background
      setTimeout(() => {
        preloadNextQuestion();
      }, 0);
    } catch (err) {
      console.error("Failed to load question:", err);
      
      // Retry logic - try up to 2 times
      if (!isRetry && retryCount < 2) {
        setRetryCount(prev => prev + 1);
        setTimeout(() => fetchQuestion(true), 1000);
        return;
      }
      
      // If genre filter fails, try without genre
      if (genre && retryCount >= 1) {
        toast.error("Genre filter unavailable, loading from all anime...");
        try {
          const res = await axios.get(`${API}/game/question`, { params: { mode, difficulty }, timeout: 15000 });
          setQuestion(res.data);
          setTimeLeft(settings.timeLimit);
          setGameStartTime(Date.now());
          return;
        } catch (fallbackErr) {
          // Continue to error state
        }
      }
      
      setLoadError(true);
      toast.error("Failed to load question. Please try again!");
    } finally {
      setLoading(false);
    }
  };

  const preloadNextQuestion = async () => {
    try {
      const params = { mode, difficulty };
      if (genre && genre !== "top100") params.genre = genre;
      if (genre === "top100") params.difficulty = "easy";
  
      const res = await axios.get(`${API}/game/question`, { params, timeout: 15000 });
      setNextQuestion(res.data);
    } catch (err) {
      console.error("Failed to preload next question:", err);
      setNextQuestion(null);
    }
  };
  
  const goToNextQuestion = () => {
    // Clear old answer/result state first
    setSelectedAnswer(null);
    setResult(null);
    setIsSubmittingAnswer(false);
    setLoadError(false);
  
    if (nextQuestion) {
      setQuestion(nextQuestion);
      setNextQuestion(null);
      setTimeLeft(settings.timeLimit);
      setGameStartTime(Date.now());
      setRetryCount(0);
  
      // Immediately start preloading the following question
      setTimeout(() => {
        preloadNextQuestion();
      }, 0);
    } else {
      fetchQuestion();
    }
  };

  useEffect(() => {
  fetchQuestion();
}, [mode, difficulty, genre, gameResetKey]);

  useEffect(() => {
    if (!question || result || loading) return;
    const timer = setInterval(() => {
      setTimeLeft((prev) => {
        if (prev <= 1) {
          handleTimeout();
          return 0;
        }
        // Play tick sound at 10 seconds or less
        if (prev <= 10) {
          playSound('tick');
        }
        return prev - 1;
      });
    }, 1000);
    return () => clearInterval(timer);
  }, [question, result, loading]);

  const handleTimeout = async () => {
    if (result) return;
    playSound('wrong');
    setResult({ correct: false, correct_answer: question?.options?.[0] || "Unknown" });
    setStreak(0);
    toast.error("Time's up!");
    setTimeout(() => {
      if (questionCount >= 9) {
        endGame();
      } else {
        setSelectedAnswer(null);
        setResult(null);
        setQuestionCount((c) => c + 1);
        goToNextQuestion();
      }
    }, 2000);
  };

  const handleAnswer = async (answer) => {
    if (selectedAnswer || result || isSubmittingAnswer) return;
  
    setSelectedAnswer(answer);
    setIsSubmittingAnswer(true);
  
    const timeTaken = (Date.now() - gameStartTime) / 1000;
  
    try {
      const res = await axios.post(`${API}/game/answer`, {
        question_id: question.id,
        answer,
        game_mode: mode,
        difficulty,
        time_taken: timeTaken
      });
  
      setResult(res.data);
  
      if (res.data.correct) {
        setCorrectAnswers((count) => count + 1);
        playSound('correct');
        const newStreak = streak + 1;
        const streakMultiplier = Math.min(newStreak, 5);
        const points = res.data.points_earned * streakMultiplier;
        setScore((s) => s + points);
        setStreak(newStreak);
        setHighestStreak((h) => Math.max(h, newStreak));
        if (newStreak > 1) playSound('streak');
        toast.success(`+${points} points! ${newStreak > 1 ? `${newStreak}x streak!` : ""}`);
      } else {
        playSound('wrong');
        setStreak(0);
        toast.error(`Wrong! It was: ${res.data.correct_answer}`);
      }
      setTimeout(() => {
        if (questionCount >= 9) {
          endGame();
        } else {
          setSelectedAnswer(null);
          setResult(null);
          setQuestionCount((c) => c + 1);
          goToNextQuestion();
        }
      }, 1200); 
    } catch (err) {
      console.error("Submit answer error:", err.response?.data || err);
      toast.error(err.response?.data?.detail || "Error submitting answer");
      setSelectedAnswer(null);
    } finally {
      setIsSubmittingAnswer(false);
    }
  };

  const endGame = async () => {
    setGameOver(true);
    playSound('gameOver');
    try {
      await axios.post(`${API}/game/end`, {
        score,
        streak: highestStreak,
        game_mode: mode,
        difficulty
      });
      if (user) fetchUser();
    } catch (err) {
      console.error("Error ending game");
    }
  };
  
const restartGame = () => {
  setScore(0);
  setStreak(0);
  setHighestStreak(0);
  setQuestionCount(0);
  setCorrectAnswers(0);
  setAccuracy(0);
  setRank("C");
  setResultMessage("");
  setSelectedAnswer(null);
  setResult(null);
  setIsSubmittingAnswer(false);
  setQuestion(null);
  setNextQuestion(null);
  setLoadError(false);
  setRetryCount(0);
  setTimeLeft(settings.timeLimit);
  setGameStartTime(null);
  setGameOver(false);
  setGameResetKey((prev) => prev + 1);
};
  
  const shareScore = (platform) => {
    const text = `🎮 I scored ${score} points in Guess the Anime! 🔥 Best streak: ${highestStreak}x\n\nCan you beat my score? Play now!`;
    const url = window.location.origin;
    
    if (platform === 'twitter') {
      window.open(`https://twitter.com/intent/tweet?text=${encodeURIComponent(text)}&url=${encodeURIComponent(url)}`, '_blank');
    } else if (platform === 'copy') {
      navigator.clipboard.writeText(`${text}\n${url}`);
      toast.success("Score copied to clipboard!");
    }
  };

if (gameOver) {
  return (
    <div className="result-overlay px-4" data-testid="game-over-screen">
      <div className="text-center animate-slide-up w-full max-w-3xl mx-auto">
        <h1 className="font-anton text-4xl md:text-7xl uppercase mb-4 animate-glitch">
          Game Over
        </h1>
        <div className="font-arcade text-3xl md:text-6xl text-[var(--primary)] mb-8 break-all">
          {score}
        </div>

        <div className="flex flex-wrap gap-6 md:gap-8 justify-center mb-8">
          <div className="text-center min-w-[100px]">
            <div className="text-[var(--neutral-500)] text-sm mb-1">Best Streak</div>
            <div className="font-arcade text-xl md:text-2xl text-[var(--secondary)]">{highestStreak}x</div>
          </div>
          <div className="text-center min-w-[100px]">
            <div className="text-[var(--neutral-500)] text-sm mb-1">Questions</div>
            <div className="font-arcade text-xl md:text-2xl">{correctAnswers}/10</div>
          </div>
          <div className="text-center min-w-[100px]">
            <div className="text-[var(--neutral-500)] text-sm mb-1">Difficulty</div>
            <div className="font-arcade text-xl md:text-2xl capitalize">{difficulty}</div>
          </div>
        </div>

        <div className="flex flex-col sm:flex-row gap-3 justify-center mb-8">
          <button
            onClick={() => shareScore('twitter')}
            className="w-full sm:w-auto px-4 py-2 bg-[#1DA1F2] text-white flex items-center justify-center gap-2 hover:opacity-90 transition-opacity"
            data-testid="share-twitter"
          >
            <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24"><path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/></svg>
            Share on X
          </button>
          <button
            onClick={() => shareScore('copy')}
            className="w-full sm:w-auto px-4 py-2 bg-white/10 text-white flex items-center justify-center gap-2 hover:bg-white/20 transition-colors border border-white/20"
            data-testid="share-copy"
          >
            <Share2 size={18} />
            Copy Score
          </button>
        </div>

        <div className="flex flex-col sm:flex-row gap-4 justify-center">
          <button onClick={restartGame} className="btn-primary justify-center" data-testid="play-again-btn">
            <Play size={20} /> Play Again
          </button>
          <Link to="/leaderboard" className="btn-secondary justify-center">
            <Trophy size={20} /> Leaderboard
          </Link>
        </div>
      </div>
    </div>
  );
}

  return (
    <div className="min-h-screen pt-20 pb-12 px-4" data-testid="game-screen">
      {/* Stats Bar */}
      <div className="max-w-4xl mx-auto mb-8">
        <div className="flex flex-wrap items-center justify-between gap-4 p-4 glass border border-white/10">
          <div className="flex items-center gap-6">
            <div className="flex items-center gap-2">
              <Target className="text-[var(--primary)]" size={20} />
              <span className="font-arcade text-sm">{questionCount + 1}/10</span>
            </div>
            <div className="flex items-center gap-2">
              <Star className="text-[var(--primary)]" size={20} />
              <span className="font-arcade text-lg text-[var(--primary)]">{score}</span>
            </div>
          </div>
          {streak > 0 && (
            <div className="streak-display animate-streak" data-testid="streak-display">
              <Flame className="text-[var(--secondary)]" size={20} />
              <span className="font-arcade text-sm">{streak}x STREAK</span>
            </div>
          )}
          <div className="flex items-center gap-4">
            <button 
              onClick={() => setSoundEnabled(!soundEnabled)} 
              className="btn-ghost p-1"
              title={soundEnabled ? "Mute sounds" : "Enable sounds"}
            >
              {soundEnabled ? <Volume2 size={18} /> : <VolumeX size={18} />}
            </button>
            <Clock className={timeLeft <= 10 ? "text-red-500" : "text-[var(--accent)]"} size={20} />
            <span className={`font-arcade text-lg ${timeLeft <= 10 ? "text-red-500 animate-pulse" : ""}`}>
              {timeLeft}s
            </span>
          </div>
        </div>
        <div className="timer-bar mt-2">
          <div className="timer-bar-fill" style={{ width: `${(timeLeft / settings.timeLimit) * 100}%` }} />
        </div>
      </div>

      {/* Question Area */}
      <div className="max-w-4xl mx-auto">
        {loading ? (
          <div className="flex flex-col items-center">
            <div className="image-container skeleton mb-8" />
            <div className="w-full space-y-3">
              {[1, 2, 3, 4].map((i) => (
                <div key={i} className="h-14 skeleton" />
              ))}
            </div>
          </div>
        ) : loadError ? (
          <div className="text-center py-12" data-testid="load-error">
            <div className="text-6xl mb-4">😢</div>
            <h3 className="font-anton text-2xl uppercase mb-2 text-[var(--secondary)]">Connection Issue</h3>
            <p className="text-[var(--neutral-500)] mb-6">
              The anime database is temporarily unavailable.<br />
              This can happen due to high traffic.
            </p>
            <div className="flex gap-4 justify-center">
              <button onClick={() => fetchQuestion()} className="btn-primary" data-testid="retry-btn">
                <Play size={18} /> Try Again
              </button>
              <Link to="/" className="btn-secondary">
                Back to Home
              </Link>
            </div>
          </div>
        ) : !question ? (
          <div className="text-center py-12">
            <p className="text-[var(--neutral-500)]">Failed to load question</p>
            <button onClick={() => fetchQuestion()} className="btn-primary mt-4">
              Try Again
            </button>
          </div>
        ) : (
          <div className="animate-slide-up" key={question?.id}>
            <div className="flex flex-col md:flex-row gap-8">
              {/* Image */}
              <div className="flex-shrink-0 mx-auto md:mx-0">
                <div className="image-container neon-border">
                  <img 
                    src={question.image_url} 
                    alt="Guess the anime" 
                    className="w-full h-full object-cover"
                    data-testid="question-image"
                  />
                </div>
              </div>

              {/* Question & Options */}
              <div className="flex-1">
                <h2 className="font-anton text-2xl md:text-3xl uppercase mb-2 text-[var(--primary)]">
                  {mode === "trivia" ? question.question : question.hint}
                </h2>
                {mode === "character" && question.character_name && (
                  <p className="text-[var(--neutral-500)] mb-6">
                    Character: <span className="text-[var(--accent)]">{question.character_name}</span>
                  </p>
                )}
                {isSubmittingAnswer && !result && (
                  <div className="mb-4 text-sm text-[var(--accent)] animate-pulse">
                    Checking answer...
                  </div>
                )}
                
                <div className="space-y-3 mt-6">
                  {question.options.map((option, idx) => (
                    <button
                      key={`${question?.id}-${idx}`}
                      onClick={() => handleAnswer(option)}
                      disabled={!!selectedAnswer || isSubmittingAnswer}
                      className={`option-btn ${
                        result 
                          ? option === result.correct_answer 
                            ? "correct" 
                            : selectedAnswer === option 
                              ? "wrong" 
                              : ""
                          : selectedAnswer === option 
                            ? "selected" 
                            : ""
                      }`}
                      data-testid={`option-${idx}`}
                    >
                      <span className="font-arcade text-xs mr-3 text-[var(--primary)]">
                        {String.fromCharCode(65 + idx)}
                      </span>
                      {option}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

// Landing Page with Mode Selection
  const LandingPage = () => {
    const { user } = useAuth();
    const navigate = useNavigate();
    const [showAuth, setShowAuth] = useState(false);
    const [selectedMode, setSelectedMode] = useState("character");
    const [selectedGenre, setSelectedGenre] = useState(null);
    const [selectedDifficulty, setSelectedDifficulty] = useState("medium");

  const genres = [
    { id: "shounen", name: "Shounen", description: "Action-packed adventures" },
    { id: "shojo", name: "Shojo", description: "Romance and drama" },
    { id: "seinen", name: "Seinen", description: "Mature themes" },
    { id: "josei", name: "Josei", description: "Mature women's stories" },
    { id: "action", name: "Action", description: "High-octane battles" },
    { id: "adventure", name: "Adventure", description: "Epic journeys" },
    { id: "comedy", name: "Comedy", description: "Laugh-out-loud fun" },
    { id: "drama", name: "Drama", description: "Emotional stories" },
    { id: "fantasy", name: "Fantasy", description: "Magic and adventure" },
    { id: "horror", name: "Horror", description: "Scary and suspenseful" },
    { id: "mystery", name: "Mystery", description: "Mind-bending puzzles" },
    { id: "romance", name: "Romance", description: "Love stories" },
    { id: "sci-fi", name: "Sci-Fi", description: "Futuristic worlds" },
    { id: "slice_of_life", name: "Slice of Life", description: "Everyday moments" },
    { id: "sports", name: "Sports", description: "Athletic competition" },
    { id: "supernatural", name: "Supernatural", description: "Paranormal phenomena" },
    { id: "mecha", name: "Mecha", description: "Giant robots" },
    { id: "isekai", name: "Isekai", description: "Transported to another world" }
  ];

  const difficulties = [
    { id: "easy", name: "Easy", time: 45, description: "Popular anime, 45 seconds", color: "#22c55e" },
    { id: "medium", name: "Medium", time: 30, description: "Mixed difficulty, 30 seconds", color: "#FACC15" },
    { id: "hard", name: "Hard", time: 20, description: "Obscure anime, 20 seconds, 6 options", color: "#ef4444" }
  ];

  const gameModes = [
    {
      id: "character",
      title: "Character Quiz",
      description: "Identify the anime from character images",
      icon: User,
      color: "var(--primary)"
    },
    {
      id: "screenshot",
      title: "Screenshot Mode",
      description: "Guess the anime from cover art",
      icon: Image,
      color: "var(--secondary)"
    },
    {
      id: "trivia",
      title: "Trivia Challenge",
      description: "Answer questions about anime details",
      icon: HelpCircle,
      color: "var(--accent)"
    }
  ];

  const handlePlay = () => {
    if (!user) {
      setShowAuth(true);
    } else {
      let params = new URLSearchParams();
      if (selectedGenre) params.append("genre", selectedGenre);
      params.append("difficulty", selectedDifficulty);
      const queryString = params.toString();
      navigate(`/play/${selectedMode}?${queryString}`);
    }
  };

  return (
    <div className="min-h-screen pt-20" data-testid="landing-page">
      {/* Hero Section */}
      <section className="hero-grid max-w-7xl mx-auto items-center">
        <div className="md:col-span-7 order-2 md:order-1">
          <span className="font-arcade text-xs text-[var(--secondary)] mb-4 block">
            ANIME QUIZ GAME
          </span>
          <h1 className="font-anton text-5xl md:text-7xl lg:text-8xl uppercase leading-none mb-6">
            Guess<br />
            <span className="text-[var(--primary)] neon-text hover:animate-glitch">The Anime</span>
          </h1>
          <p className="text-lg text-[var(--neutral-500)] max-w-md mb-8">
            Test your knowledge! Identify anime from characters, screenshots, and trivia questions. 
            Compete for the top spot on the leaderboard.
          </p>

          <div className="flex flex-wrap gap-4">
            <button 
              onClick={handlePlay} 
              className="btn-primary btn-arcade"
              data-testid="start-game-btn"
            >
              <Play size={20} /> Start Playing
            </button>
            <Link to="/leaderboard" className="btn-secondary">
              <Trophy size={20} /> Leaderboard
            </Link>
          </div>

          {user && (
            <div className="mt-8 p-4 glass border border-white/10 inline-block">
              <div className="text-[var(--neutral-500)] text-sm">Your Score</div>
              <div className="font-arcade text-2xl text-[var(--primary)]">{user.total_score}</div>
            </div>
          )}
        </div>

        <div className="hidden md:flex md:col-span-5 order-1 md:order-2 justify-center">
          <div className="relative">
            <div className="absolute inset-0 bg-gradient-to-br from-[var(--primary)] to-[var(--secondary)] blur-3xl opacity-40" />
            <img 
              src="/heroavatar.png"
              alt="Anime Character"
              className="relative z-10 max-w-sm w-full animate-float rounded-lg"
              data-testid="hero-image"
              loading="lazy"
              decoding="async"
            />
          </div>
        </div>
      </section>

      {/* Genre Filter Section */}
      <section className="max-w-7xl mx-auto px-6 py-12">
        <h2 className="font-anton text-2xl md:text-3xl uppercase mb-6 text-center">
          Filter by <span className="text-[var(--secondary)]">Genre</span>
        </h2>
        <div className="flex flex-wrap justify-center gap-3">
          <button
            onClick={() => setSelectedGenre(null)}
            className={`px-4 py-2 text-sm font-medium transition-all ${
              selectedGenre === null 
                ? "bg-[var(--primary)] text-black" 
                : "bg-white/5 text-[var(--neutral-100)] hover:bg-white/10 border border-white/10"
            }`}
            data-testid="genre-all"
          >
            All Genres
          </button>
          <button
            onClick={() => setSelectedGenre("top100")}
            className={`px-4 py-2 text-sm font-medium transition-all ${
              selectedGenre === "top100" 
                ? "bg-[var(--accent)] text-black" 
                : "bg-white/5 text-[var(--neutral-100)] hover:bg-white/10 border border-white/10"
            }`}
            data-testid="genre-top100"
          >
            ⭐ Top 100 Anime
          </button>
          {genres.map((genre) => (
            <button
              key={genre.id}
              onClick={() => setSelectedGenre(genre.id)}
              className={`px-4 py-2 text-sm font-medium transition-all ${
                selectedGenre === genre.id 
                  ? "bg-[var(--primary)] text-black" 
                  : "bg-white/5 text-[var(--neutral-100)] hover:bg-white/10 border border-white/10"
              }`}
              data-testid={`genre-${genre.id}`}
              title={genre.description}
            >
              {genre.name}
            </button>
          ))}
        </div>
        {selectedGenre && (
          <p className="text-center text-[var(--neutral-500)] mt-4 text-sm">
            Questions will be filtered to <span className="text-[var(--primary)]">
              {selectedGenre === "top100" ? "Top 100 Anime" : genres.find(g => g.id === selectedGenre)?.name}
            </span> anime
          </p>
        )}
      </section>

      {/* Difficulty Section */}
      <section className="max-w-7xl mx-auto px-6 py-8">
        <h2 className="font-anton text-2xl md:text-3xl uppercase mb-6 text-center">
          Select <span className="text-[var(--accent)]">Difficulty</span>
        </h2>
        <div className="flex flex-wrap justify-center gap-4">
          {difficulties.map((diff) => (
            <button
              key={diff.id}
              onClick={() => setSelectedDifficulty(diff.id)}
              className={`px-6 py-4 text-center transition-all border ${
                selectedDifficulty === diff.id 
                  ? "border-2" 
                  : "bg-white/5 border-white/10 hover:bg-white/10"
              }`}
              style={{
                borderColor: selectedDifficulty === diff.id ? diff.color : undefined,
                boxShadow: selectedDifficulty === diff.id ? `0 0 20px ${diff.color}40` : undefined
              }}
              data-testid={`difficulty-${diff.id}`}
            >
              <div className="font-anton text-lg uppercase mb-1" style={{ color: diff.color }}>
                {diff.name}
              </div>
              <div className="text-[var(--neutral-500)] text-xs">{diff.description}</div>
              <div className="font-arcade text-xs mt-2" style={{ color: diff.color }}>
                <Clock size={12} className="inline mr-1" />{diff.time}s
              </div>
            </button>
          ))}
        </div>
      </section>

      {/* Game Modes Section */}
      <section className="max-w-7xl mx-auto px-6 py-12">
        <h2 className="font-anton text-3xl md:text-4xl uppercase mb-12 text-center">
          Choose Your <span className="text-[var(--primary)]">Challenge</span>
        </h2>
        
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {gameModes.map((mode) => (
            <button
              key={mode.id}
              onClick={() => setSelectedMode(mode.id)}
              className={`mode-card text-left ${selectedMode === mode.id ? "active" : ""}`}
              data-testid={`mode-${mode.id}`}
            >
              <mode.icon 
                size={48} 
                className="mb-4" 
                style={{ color: mode.color }} 
              />
              <h3 className="font-anton text-xl uppercase mb-2">{mode.title}</h3>
              <p className="text-[var(--neutral-500)]">{mode.description}</p>
              {selectedMode === mode.id && (
                <div className="absolute top-4 right-4">
                  <ChevronRight className="text-[var(--primary)]" size={24} />
                </div>
              )}
            </button>
          ))}
        </div>

        <div className="text-center mt-12">
          <button 
            onClick={handlePlay} 
            className="btn-primary btn-arcade text-lg px-8 py-4"
            data-testid="play-selected-mode-btn"
          >
            <Play size={24} /> Play {gameModes.find(m => m.id === selectedMode)?.title}
          </button>
          <p className="text-[var(--neutral-500)] text-sm mt-4">
            {selectedDifficulty === "easy" && "🟢 Easy Mode"}
            {selectedDifficulty === "medium" && "🟡 Medium Mode"}
            {selectedDifficulty === "hard" && "🔴 Hard Mode"}
            {selectedGenre && (
              <span className="ml-2">
                • {selectedGenre === "top100" ? "Top 100 Anime" : genres.find(g => g.id === selectedGenre)?.name}
              </span>
            )}
          </p>
        </div>
      </section>

      {/* Stats Section */}
      <section className="max-w-7xl mx-auto px-6 py-12 mb-12">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
          {[
            { label: "Game Modes", value: "3", icon: Gamepad2 },
            { label: "Genres", value: "18", icon: HelpCircle },
            { label: "Anime Covered", value: "250+", icon: Star },
            { label: "Active Players", value: "Daily", icon: User }
          ].map((stat, idx) => (
            <div key={idx} className="glass border border-white/10 p-6 text-center stat-card">
              <stat.icon className="mx-auto mb-3 text-[var(--primary)]" size={32} />
              <div className="font-arcade text-2xl text-[var(--primary)]">{stat.value}</div>
              <div className="text-[var(--neutral-500)] text-sm mt-1">{stat.label}</div>
            </div>
          ))}
        </div>
      </section>

      <AuthModal isOpen={showAuth} onClose={() => setShowAuth(false)} />
    </div>
  );
};

// Leaderboard Page
// ============================================
// MOBILE-OPTIMIZED LEADERBOARD COMPONENT
// Replace your current LeaderboardPage component with this
// ============================================

const LeaderboardPage = () => {
  const [leaderboard, setLeaderboard] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedPlayer, setExpandedPlayer] = useState(null);
  const { user } = useAuth();

  useEffect(() => {
    fetchLeaderboard();
  }, []);

  const fetchLeaderboard = async () => {
    try {
      const res = await axios.get(`${API}/leaderboard`, { params: { limit: 50 } });
      setLeaderboard(res.data);
    } catch (err) {
      toast.error("Failed to load leaderboard");
    } finally {
      setLoading(false);
    }
  };

  const getRankIcon = (rank) => {
    if (rank === 1) return <Crown className="text-yellow-400 rank-crown" size={28} />;
    if (rank === 2) return <Crown className="text-gray-400 rank-crown" size={24} />;
    if (rank === 3) return <Crown className="text-amber-600 rank-crown" size={24} />;
    return <span className="font-arcade text-lg font-bold text-[var(--neutral-500)]">#{rank}</span>;
  };

  const isCurrentUser = (player) => {
    return user && user.username === player.username;
  };

  return (
    <div className="min-h-screen pt-24 pb-12 px-4" data-testid="leaderboard-page">
      <div className="max-w-4xl mx-auto">
        <div className="text-center mb-12">
          <h1 className="font-anton text-4xl md:text-6xl uppercase mb-4">
            <span className="text-[var(--primary)]">Top</span> Players
          </h1>
          <p className="text-[var(--neutral-500)]">The ultimate anime experts</p>
        </div>

        {loading ? (
          <div className="space-y-4">
            {[1, 2, 3, 4, 5].map((i) => (
              <div key={i} className="skeleton" />
            ))}
          </div>
        ) : (
          <>
            {/* Desktop: Table Header */}
            <div className="glass border border-white/10 overflow-hidden hidden md:block">
              <div className="leaderboard-row leaderboard-header text-[var(--neutral-500)] text-sm font-bold border-b border-white/10">
                <div>Rank</div>
                <div>Player</div>
                <div>Score</div>
                <div>Games</div>
                <div>Streak</div>
              </div>

              {/* Desktop: Table Rows */}
              {leaderboard.map((player, idx) => (
                <div 
                  key={idx} 
                  className={`leaderboard-row ${player.rank <= 3 ? "top-3" : ""} ${
                    isCurrentUser(player) ? "bg-[var(--primary)]/10 current-user" : ""
                  }`}
                  data-testid={`leaderboard-row-${player.rank}`}
                >
                  <div className="flex justify-center">{getRankIcon(player.rank)}</div>
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 rounded-full bg-[var(--secondary)] flex items-center justify-center text-white font-bold text-sm">
                      {player.username[0].toUpperCase()}
                    </div>
                    <span className="font-medium">
                      {player.username}
                      {player.is_guest && <span className="text-[var(--neutral-500)] text-xs ml-2">(Guest)</span>}
                    </span>
                  </div>
                  <div className="font-arcade text-[var(--primary)]">{player.total_score.toLocaleString()}</div>
                  <div className="text-[var(--neutral-500)]">{player.games_played}</div>
                  <div className="flex items-center gap-1">
                    <Flame className="text-[var(--secondary)]" size={16} />
                    {player.highest_streak}x
                  </div>
                </div>
              ))}
            </div>

            {/* Mobile: Card Layout */}
            <div className="md:hidden space-y-3">
              {leaderboard.map((player, idx) => {
                const isExpanded = expandedPlayer === player.rank;
            
                return (
                  <button
                    key={idx}
                    type="button"
                    onClick={() =>
                      setExpandedPlayer(isExpanded ? null : player.rank)
                    }
                    className={`w-full text-left p-4 glass border border-white/10 transition-all ${
                      player.rank <= 3 ? "top-3" : ""
                    } ${isCurrentUser(player) ? "current-user" : ""}`}
                    data-testid={`leaderboard-card-${player.rank}`}
                  >
                    <div className="flex items-center gap-3">
                      <div className="flex-shrink-0 w-10 flex justify-center">
                        {getRankIcon(player.rank)}
                      </div>
            
                      <div className="w-10 h-10 rounded-full bg-[var(--secondary)] flex items-center justify-center text-white font-bold text-sm flex-shrink-0">
                        {player.username[0].toUpperCase()}
                      </div>
            
                      <div className="min-w-0 flex-1">
                        <div className="font-medium truncate leading-tight">
                          {player.username}
                          {isCurrentUser(player) && (
                            <span className="ml-2 text-xs text-[var(--primary)]">YOU</span>
                          )}
                        </div>
                        {player.is_guest && (
                          <div className="text-[var(--neutral-500)] text-xs mt-1">(Guest)</div>
                        )}
                      </div>
            
                      <div className="flex-shrink-0 text-right ml-2">
                        <div className="text-[10px] text-[var(--neutral-500)] uppercase tracking-wide">Score</div>
                        <div className="font-arcade text-sm text-[var(--primary)] whitespace-nowrap">
                          {player.total_score.toLocaleString()}
                        </div>
                      </div>
                    </div>
            
                    {isExpanded && (
                      <div className="grid grid-cols-2 gap-2 mt-4">
                        <div className="bg-black/20 border border-white/5 px-3 py-3 text-center min-w-0">
                          <div className="text-[10px] text-[var(--neutral-500)] mb-1 uppercase tracking-wide">Games</div>
                          <div className="font-arcade text-xs whitespace-nowrap">
                            {player.games_played}
                          </div>
                        </div>
            
                        <div className="bg-black/20 border border-white/5 px-3 py-3 text-center min-w-0">
                          <div className="text-[10px] text-[var(--neutral-500)] mb-1 uppercase tracking-wide">Streak</div>
                          <div className="font-arcade text-xs text-[var(--secondary)] flex items-center justify-center gap-1 whitespace-nowrap">
                            <Flame size={12} />
                            {player.highest_streak}x
                          </div>
                        </div>
                      </div>
                    )}
                  </button>
                );
              })}
            </div>
          </>
        )}

        {/* Empty State */}
        {!loading && leaderboard.length === 0 && (
          <div className="text-center py-12">
            <Trophy className="mx-auto mb-4 text-[var(--neutral-500)]" size={48} />
            <p className="text-[var(--neutral-500)]">No players yet. Be the first!</p>
          </div>
        )}
      </div>
    </div>
  );
};

// Profile Page
const ProfilePage = () => {
  const { user, logout } = useAuth();
  const [stats, setStats] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    if (!user) {
      navigate("/");
      return;
    }
    fetchStats();
  }, [user]);

  const fetchStats = async () => {
    try {
      const res = await axios.get(`${API}/stats/me`);
      setStats(res.data);
    } catch (err) {
      console.error("Failed to load stats");
    }
  };

  const shareProfile = () => {
    const text = `🎮 My Guess the Anime stats:\n⭐ ${user.total_score} points\n🎯 ${user.games_played} games\n🔥 ${user.highest_streak}x best streak\n\nCan you beat me?`;
    const url = window.location.origin;
    navigator.clipboard.writeText(`${text}\n${url}`);
    toast.success("Stats copied to clipboard!");
  };

  if (!user) return null;

  return (
    <div className="min-h-screen pt-24 pb-12 px-4" data-testid="profile-page">
      <div className="max-w-4xl mx-auto">
        <div className="glass border border-white/10 p-8 mb-8">
          <div className="flex items-center justify-between mb-8">
            <div className="flex items-center gap-6">
              <div className="w-20 h-20 rounded-full bg-[var(--primary)] flex items-center justify-center text-black font-bold text-3xl">
                {user.username[0].toUpperCase()}
              </div>
              <div>
                <h1 className="font-anton text-3xl uppercase">{user.username}</h1>
                <p className="text-[var(--neutral-500)]">
                  {user.is_guest ? "Guest Player" : user.email}
                </p>
              </div>
            </div>
            <button 
              onClick={shareProfile}
              className="btn-ghost flex items-center gap-2"
              data-testid="share-profile"
            >
              <Share2 size={18} /> Share
            </button>
          </div>

          <div className="grid grid-cols-3 gap-6">
            <div className="text-center p-4 bg-black/20 border border-white/5">
              <div className="font-arcade text-3xl text-[var(--primary)]">{user.total_score}</div>
              <div className="text-[var(--neutral-500)] text-sm">Total Score</div>
            </div>
            <div className="text-center p-4 bg-black/20 border border-white/5">
              <div className="font-arcade text-3xl">{user.games_played}</div>
              <div className="text-[var(--neutral-500)] text-sm">Games Played</div>
            </div>
            <div className="text-center p-4 bg-black/20 border border-white/5">
              <div className="font-arcade text-3xl text-[var(--secondary)] flex items-center justify-center gap-2">
                <Flame size={24} /> {user.highest_streak}x
              </div>
              <div className="text-[var(--neutral-500)] text-sm">Best Streak</div>
            </div>
          </div>
        </div>

        {/* Achievements Section */}
        {stats?.achievements && (
          <div className="glass border border-white/10 p-8 mb-8">
            <div className="flex items-center justify-between mb-6">
              <h2 className="font-anton text-xl uppercase">Achievements</h2>
              <span className="text-[var(--neutral-500)] text-sm">
                {stats.achievements.length} / 10 unlocked
              </span>
            </div>
            
            {stats.achievements.length > 0 ? (
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4 mb-6">
                {stats.achievements.map((ach, idx) => (
                  <div 
                    key={idx} 
                    className="text-center p-4 bg-[var(--primary)]/10 border border-[var(--primary)]/30 hover:border-[var(--primary)] transition-colors"
                    title={ach.description}
                  >
                    <div className="text-3xl mb-2">{ach.icon}</div>
                    <div className="font-medium text-sm text-[var(--primary)]">{ach.name}</div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-[var(--neutral-500)] text-center py-4">
                Play games to unlock achievements!
              </p>
            )}

            {stats.next_achievements?.length > 0 && (
              <div>
                <h3 className="text-[var(--neutral-500)] text-sm mb-3">Next to unlock:</h3>
                <div className="flex flex-wrap gap-3">
                  {stats.next_achievements.map((ach, idx) => (
                    <div 
                      key={idx}
                      className="flex items-center gap-2 px-3 py-2 bg-white/5 border border-white/10 text-sm"
                      title={ach.description}
                    >
                      <span className="opacity-50">{ach.icon}</span>
                      <span className="text-[var(--neutral-500)]">{ach.name}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {stats?.recent_games?.length > 0 && (
          <div className="glass border border-white/10 p-8 mb-8">
            <h2 className="font-anton text-xl uppercase mb-6">Recent Games</h2>
            <div className="space-y-4">
              {stats.recent_games.map((game, idx) => (
                <div key={idx} className="flex items-center justify-between p-4 bg-black/20 border border-white/5">
                  <div>
                    <span className="text-[var(--primary)] capitalize">{game.game_mode}</span>
                    <span className="text-[var(--neutral-500)] text-sm ml-4">
                      {new Date(game.played_at).toLocaleDateString()}
                    </span>
                  </div>
                  <div className="flex items-center gap-4">
                    <span className="font-arcade">{game.score}</span>
                    <span className="text-[var(--secondary)] flex items-center gap-1">
                      <Flame size={16} /> {game.streak}x
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        <div className="mt-8 flex gap-4">
          <Link to="/" className="btn-primary">
            <Play size={20} /> Play Again
          </Link>
          <button onClick={logout} className="btn-secondary">
            <LogOut size={20} /> Logout
          </button>
        </div>
      </div>
    </div>
  );
};

// Game Page Wrapper
const GamePage = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [showAuth, setShowAuth] = useState(false);
  
  // Read URL params directly on every render (no useState!)
  const pathParts = window.location.pathname.split("/");
  const mode = pathParts[pathParts.length - 1] || "character";
  const urlParams = new URLSearchParams(window.location.search);
  const genre = urlParams.get("genre");
  const difficulty = urlParams.get("difficulty") || "medium";

  useEffect(() => {
    if (!user) {
      setShowAuth(true);
    }
  }, [user]);

  if (!user && !showAuth) {
    navigate("/");
    return null;
  }

  return (
    <>
      {user ? (
        <GameScreen mode={mode} genre={genre} difficulty={difficulty} />
      ) : (
        <AuthModal isOpen={showAuth} onClose={() => navigate("/")} />
      )}
    </>
  );
};

// Main App
function App() {
  const [showAuth, setShowAuth] = useState(false);

  return (
    <AuthProvider>
      <div className="app-container noise-overlay">
        <div className="app-background cyber-grid" />
        <BrowserRouter>
          <Navigation onAuthClick={() => setShowAuth(true)} />
          <Routes>
            <Route path="/" element={<LandingPage />} />
            <Route path="/play/:mode" element={<GamePage />} />
            <Route path="/leaderboard" element={<LeaderboardPage />} />
            <Route path="/profile" element={<ProfilePage />} />
          </Routes>
          <AuthModal isOpen={showAuth} onClose={() => setShowAuth(false)} />
        </BrowserRouter>
        <Toaster 
          position="top-center" 
          toastOptions={{
            style: {
              background: '#18181B',
              color: '#F4F4F5',
              border: '1px solid rgba(255,255,255,0.1)',
              fontFamily: 'Manrope, sans-serif'
            }
          }}
        />
      </div>
    </AuthProvider>
  );
}

export default App;
